// Merlin Watches — "tell Merlin what to watch, get a short dated readout back"
// (kanban 8e4296bc, AI/Code check-in 2026-10-12).
//
// The reader-facing home of the watches is the Briefing page, where they sit
// as tabs (the daily briefing first, kanban 001b6e2b) beside the one Merlin
// chat that changes them (kanban 14927fad: "as long as they have a chat
// interface to update it").
// This page is the admin's oversight view (everyone's watches, the lifecycle
// buttons) and the old portal route; it points at the chat for refining.
// Every control carries a data-action so the usage graph sees it.

import { useState } from 'react';
import { WatchNewForm } from '../components/merlinWatches/WatchNewForm';
import { WatchPanel } from '../components/merlinWatches/WatchPanel';
import { WatchRail } from '../components/merlinWatches/WatchRail';
import { NEW_WATCH, useWatches } from '../components/merlinWatches/useWatches';
import styles from '../components/merlinWatches/watches.module.css';
import { useAuth } from '../context/AuthContext';

export function MerlinWatchesPage({ variant = 'admin' }: { variant?: 'admin' | 'portal' } = {}) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isPortal = variant === 'portal';
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const w = useWatches(scope, { autoSelect: 'newest' });

  const watch = w.detail?.watch ?? null;
  const isOwner = !!watch && !!user && watch.owner_user_id === user.id;
  const chatPath = `${isPortal ? '/portal' : ''}/signals/digest${watch ? `?watch=${watch.id}` : ''}`;

  return (
    <div className={`${styles.page} ${isPortal ? styles.pagePortal : ''}`}>
      <header className={styles.head}>
        <div className={styles.headRow}>
          <div>
            <h1>What Merlin is watching for you</h1>
            <p>
              Say what you want watched, in your own words, and how often. Merlin reads the data
              behind it and writes a short readout at that cadence — numbers, a chart or two, and
              a blunt read on what is better and what is worse. To change a readout, tell Merlin
              in the chat on your Briefing page.
            </p>
          </div>
          {isAdmin && (
            <label className={styles.scopeToggle}>
              <input
                type="checkbox"
                checked={scope === 'all'}
                onChange={(e) => setScope(e.target.checked ? 'all' : 'mine')}
                data-action="watch_scope_all"
                data-record-value="scope"
                data-outcome="request"
              />
              Everyone&apos;s watches
            </label>
          )}
        </div>
        {w.note && <div className={styles.note}>{w.note}</div>}
      </header>

      {w.loading && <div className={styles.muted}>Loading…</div>}
      {w.error && <div className={styles.error}>{w.error}</div>}

      {!w.loading && !w.error && (
        <div className={styles.grid}>
          <WatchRail
            items={w.sorted}
            selected={w.selected}
            onSelect={w.select}
            showOwner={scope === 'all'}
            ariaLabel="Your watches"
          />
          <section className={styles.main}>
            {w.selected === NEW_WATCH && <WatchNewForm busy={w.busy} onCreate={w.createWatch} />}
            {w.selected !== NEW_WATCH && w.detail && (
              <WatchPanel
                detail={w.detail}
                html={w.html}
                isOwner={isOwner}
                busy={w.busy}
                showOwner={scope === 'all'}
                onCadence={(c) => void w.setCadence(c)}
                onRunNow={() => void w.runNow()}
                onTogglePause={() => void w.togglePause()}
                onRemove={w.remove}
                refineSlot={
                  isOwner ? (
                    <p className={styles.chatPointer}>
                      To change what this readout shows, or how it reads (“bullet points for
                      this section”), tell Merlin in the chat on your Briefing page; every
                      later check keeps it that way.{' '}
                      <a href={chatPath} data-action="watch_open_chat" data-outcome="navigate">
                        Open the chat ↗
                      </a>
                    </p>
                  ) : undefined
                }
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
}
